/**
 * Journal d'erreurs de la PWA -- PRIS au socle (`@santvia/ui-socle`, `error-journal`), jamais reecrit
 * (CDC § « Les pages d'erreur et le journal d'erreurs viennent du socle », porteur 2026-10-14).
 *
 * Le socle capte (erreurs non rattrapees, promesses rejetees) et masque a l'ecriture ; Klavox decide de deux
 * choses seulement :
 *
 * - **ou partent les lignes** : nulle part hors du canal bêta (decision du porteur, 2026-10-14 : « si release rien
 *   du tout »). Le predicat echoue FERME -- tout ce qui n'est pas exactement `beta` est release, et en release le
 *   socle ne recoit meme pas de `sink` : le journal reste en memoire, local ;
 * - **un masquage de plus**, propre a un coffre zero-knowledge : un message d'erreur peut citer la donnee qui l'a
 *   provoquee. V8 ecrit `Unexpected token 'h', "{"pw":hunter2}" is not valid JSON` -- un fragment d'entree
 *   dechiffree, que le masquage du socle (jetons, adresses, chemins) laisse passer. Klavox retire donc tout
 *   litteral cite AVANT que la ligne n'atteigne le socle : a l'ecriture, jamais a la lecture.
 */
import { captureWindowErrors, createErrorJournal, type ErrorJournal } from '@santvia/ui-socle';

/** Le seul canal qui autorise une remontee. Toute autre valeur -- absente, `Beta`, ` beta`, `1` -- vaut release. */
export const CANAL_BETA = 'beta';

export function remonteeAutorisee(canal: unknown): boolean {
  return canal === CANAL_BETA;
}

/**
 * Retire tout litteral cite d'un detail d'erreur : du premier guillemet au dernier, d'un seul tenant. Glouton
 * expres -- un message tronque (`"{"pw":"aaaa... is not valid JSON`) a des guillemets desequilibres, et masquer
 * trop un message d'erreur coute moins qu'en laisser fuir la moitie d'un secret.
 */
export function retirerLitteraux(detail: string): string {
  return detail.replace(/["'`«“‘][\s\S]*["'`»”’]/g, '<…>');
}

/** Le journal que voit le socle : chaque detail passe par `retirerLitteraux` avant son propre masquage. */
export function journalSansLitteraux(journal: ErrorJournal): ErrorJournal {
  return {
    record: (kind, detail = '') => journal.record(kind, retirerLitteraux(detail)),
    text: () => journal.text(),
  };
}

/**
 * La fenetre telle que le socle la type. ⚠️ Le vrai `window` n'y entre pas : `WindowLike.removeEventListener` attend un
 * ecouteur `(event: never) => void`, que l'ecouteur `(ev: any)` du DOM ne satisfait pas (TS2345, socle 0.24.0).
 * Defaut remonte au producteur du socle ; en attendant, `installerJournalErreurs` accepte aussi `Window` et le
 * transtype -- au typage seulement, le socle n'appelle que `addEventListener`/`removeEventListener`.
 */
type FenetreJournal = Parameters<typeof captureWindowErrors>[0];

export interface OptionsJournalErreurs {
  /** Canal de la construction ; seul `beta` ouvre la remontee. */
  readonly canal?: unknown;
  /** Destination de la remontee en bêta. Ignoree hors bêta : elle n'est meme pas transmise au socle. */
  readonly transport?: (ligne: string) => void;
  readonly version?: string;
  readonly clock?: () => Date;
}

export interface JournalErreurs {
  readonly journal: ErrorJournal;
  /** Debranche la capture (tests, demontage). */
  readonly debrancher: () => void;
}

/** Branche le journal sur la fenetre : capture sans geste, masquage a l'ecriture, remontee seulement en bêta. */
export function installerJournalErreurs(fenetre: FenetreJournal | Window, options: OptionsJournalErreurs = {}): JournalErreurs {
  const journal = journalSansLitteraux(
    createErrorJournal({
      product: 'klavox',
      version: options.version,
      sink: remonteeAutorisee(options.canal) ? options.transport : undefined,
      clock: options.clock,
    }),
  );
  return { journal, debrancher: captureWindowErrors(fenetre as FenetreJournal, journal) };
}
